// Staff reservation writes go through newFunctions (fix 6), not client Firestore:
//   POST  /business/:bizId/reservations/phone            (staff phone booking)
//   PATCH /business/:bizId/reservations/:reservationId   (edits and status)
// Auth: Firebase ID token; caller must be Staff+ at the venue (else 403).
// The server sets reservationId, bizId (doc ID), bizName, bizAddress, source,
// payAmount, date, createdBy*; those are never sent from here.

import firebase from "firebase/app";
import "firebase/auth";
import { ENDPOINTS } from "../config";

export const PHONE_BOOKING_STATUSES = ["pending", "confirmed"];
export const EDITABLE_STATUSES = ["pending", "confirmed", "fulfilled", "expired", "canceled"];
// canceled and removed are final: the API rejects any later change.
export const FINAL_STATUSES = ["canceled", "removed"];

/** Delete = "removed" for staff phone bookings, else "canceled" (refunds any deposit). */
export const deleteStatusFor = (reservation) =>
  reservation?.source === "venue_phone" ? "removed" : "canceled";

/** Confirmation shown before cancelling/removing. */
export function cancelConfirmMessage(reservation) {
  if (reservation?.source === "venue_phone") return "Remove this phone booking?";
  const deposit = Number(reservation?.payAmount) || 0;
  return deposit > 0
    ? `Cancel this booking? The customer's $${deposit % 1 ? deposit.toFixed(2) : deposit} deposit will be refunded.`
    : "Cancel this booking?";
}

export const REFUND_FAILED_MESSAGE =
  "The deposit couldn't be refunded, so the booking wasn't canceled. Try again.";

// Allowed on any reservation at the venue.
const BOOKING_FIELDS = ["reservationDate", "timeFrom", "timeTo", "numberOfPeople", "area", "tableMode", "occasion", "reservationStatus"];
// Only editable on source 'venue_phone' bookings (on app bookings these are the customer's own details).
export const CUSTOMER_FIELDS = ["userFirstName", "userLastName", "username", "userPhone", "userEmail", "accessibilityNotes"];

const OPTIONAL_TEXT = ["area", "tableMode", "occasion", "accessibilityNotes", "userEmail", "userFirstName", "userLastName", "timeTo"];

const clean = (v) => (typeof v === "string" ? v.trim() : v);

/** Body for POST /reservations/phone from the dashboard modal's state. */
export function buildPhoneBookingBody(form) {
  const body = {
    reservationDate: clean(form.reservationDate),
    timeFrom: clean(form.timeFrom),
    numberOfPeople: Number(form.numberOfPeople),
    userPhone: clean(form.userPhone),
  };
  if (clean(form.username)) body.username = clean(form.username);
  OPTIONAL_TEXT.forEach((k) => {
    const v = clean(form[k]);
    if (v !== undefined && v !== null && v !== "") body[k] = v;
  });
  if (PHONE_BOOKING_STATUSES.includes(form.reservationStatus)) {
    body.reservationStatus = form.reservationStatus;
  }
  return body;
}

/**
 * Only the fields that changed (PATCH semantics). Customer fields are only
 * included for venue phone bookings; on app bookings they're the customer's.
 */
export function diffReservation(original, edited) {
  const isPhoneBooking = original?.source === "venue_phone";
  const fields = isPhoneBooking ? [...BOOKING_FIELDS, ...CUSTOMER_FIELDS] : BOOKING_FIELDS;
  const changes = {};
  fields.forEach((k) => {
    let a = clean(original?.[k]);
    let b = clean(edited?.[k]);
    if (k === "numberOfPeople") {
      a = a === undefined || a === "" ? a : Number(a);
      b = b === undefined || b === "" ? b : Number(b);
    }
    if ((a ?? "") !== (b ?? "")) changes[k] = b;
  });
  return changes;
}

export const DEFAULT_DURATION_MIN = 120;
export const MAX_DURATION_MIN = 12 * 60;

const toMin = (hhmm) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** "HH:mm" + minutes, wrapping past midnight ("23:30" + 120 → "01:30"). */
export function addMinutesHHmm(hhmm, minutes) {
  const t = (((toMin(hhmm) + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}

/** Minutes from `from` to `to`, treating an earlier `to` as the next day. */
export const durationMinutes = (from, to) => (toMin(to) - toMin(from) + 1440) % 1440;

/** Client-side check matching the server's required fields. Returns a list of problems. */
export function validatePhoneBooking(form) {
  const problems = [];
  if (!clean(form.reservationDate) || Number.isNaN(Date.parse(form.reservationDate))) problems.push("Date");
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(clean(form.timeFrom) || "")) problems.push("Time From");
  if (clean(form.timeTo) && !/^([01]\d|2[0-3]):[0-5]\d$/.test(clean(form.timeTo))) problems.push("Time To");
  else if (clean(form.timeTo) && /^([01]\d|2[0-3]):[0-5]\d$/.test(clean(form.timeFrom) || "")) {
    // Past midnight is fine (22:00–01:00); equal times or > 12h usually mean an AM/PM slip.
    const d = durationMinutes(clean(form.timeFrom), clean(form.timeTo));
    if (d === 0 || d > MAX_DURATION_MIN) problems.push("Time To must be after Time From (up to 12 hours later)");
  }
  const n = Number(form.numberOfPeople);
  if (!Number.isInteger(n) || n < 1 || n > 100) problems.push("No of People (1–100)");
  if (!clean(form.userPhone)) problems.push("Customer Phone");
  if (!clean(form.username) && !clean(form.userFirstName) && !clean(form.userLastName)) problems.push("Customer");
  return problems;
}

export class ReservationApiError extends Error {
  constructor(status, body) {
    const fields = Array.isArray(body?.fields) ? body.fields : [];
    const detail = fields.length
      ? fields.map((f) => `${f.field}: ${f.message}`).join("; ")
      : body?.message || body?.error || body?.code || `HTTP ${status}`;
    super(
      status === 403
        ? "You don't have permission to manage reservations at this venue."
        : body?.code === "REFUND_FAILED"
        ? REFUND_FAILED_MESSAGE
        : detail
    );
    this.status = status;
    this.code = body?.code;
    this.fields = fields;
  }
}

async function call(method, url, body) {
  const user = firebase.auth().currentUser;
  if (!user) throw new ReservationApiError(401, { message: "Please sign in again." });
  const token = await user.getIdToken();
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  if (!res.ok) throw new ReservationApiError(res.status, json);
  return json;
}

export const createPhoneBooking = (bizId, form) =>
  call("POST", `${ENDPOINTS.business(bizId)}/reservations/phone`, buildPhoneBookingBody(form));

export const updateReservation = (bizId, reservationId, changes) =>
  call("PATCH", `${ENDPOINTS.business(bizId)}/reservations/${encodeURIComponent(reservationId)}`, changes);
